import intlTelInput from "intl-tel-input";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import "intl-tel-input/build/css/intlTelInput.css";
import "../css/Login.css";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");
const LOGIN_API = `${API_BASE_URL}/api/login`;

// ✅ Allow both "admin" and "super admin"
const REQUIRE_SUPER_ONLY = false;

/* ---------------- Countries & rules ---------------- */
const COUNTRY_RULES = {
  bt: { dial: "975", national: /^(17|16|77)\d{6}$/ },
  in: { dial: "91", national: /^[6-9]\d{9}$/ },
  np: { dial: "977", national: /^(97|98|96)\d{8}$/ },
  bd: { dial: "880", national: /^1[3-9]\d{8}$/ },
  pk: { dial: "92", national: /^3\d{9}$/ },
  lk: { dial: "94", national: /^7\d{8}$/ },
  th: { dial: "66", national: /^(6|8|9)\d{8}$/ },
  my: { dial: "60", national: /^\d{9,10}$/ },
  sg: { dial: "65", national: /^[689]\d{7}$/ },
  id: { dial: "62", national: /^\d{9,11}$/ },
  ph: { dial: "63", national: /^9\d{9}$/ },
  vn: { dial: "84", national: /^(3|5|7|8|9)\d{8}$/ },
  cn: { dial: "86", national: /^1\d{10}$/ },
  jp: { dial: "81", national: /^0?\d{9,10}$/ },
  kr: { dial: "82", national: /^0?1\d{8,9}$/ },
  mm: { dial: "95", national: /^9\d{8,10}$/ },
};
const ONLY_COUNTRIES = ["bt", "in", "np", "bd", "pk", "lk", "th", "my", "sg", "id", "ph", "vn", "cn", "jp", "kr", "mm"];

/* ---------------- localStorage write blocker ---------------- */
function blockLocalStorageWrites() {
  const original = {
    setItem: localStorage.setItem.bind(localStorage),
    removeItem: localStorage.removeItem.bind(localStorage),
    clear: localStorage.clear.bind(localStorage),
  };
  localStorage.setItem = () => { };
  localStorage.removeItem = () => { };
  localStorage.clear = () => { };
  return () => {
    localStorage.setItem = original.setItem;
    localStorage.removeItem = original.removeItem;
    localStorage.clear = original.clear;
  };
}

/* ---------------- Helpers to preserve "seen" keys ---------------- */
const isSeenKey = (k = "") => k.startsWith("admin_notif_seen_ts_");
function snapshotSeenKeys() {
  const keep = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && isSeenKey(k)) keep[k] = localStorage.getItem(k);
    }
  } catch { }
  return keep;
}
function restoreSeenKeys(map) {
  try {
    Object.entries(map || {}).forEach(([k, v]) => localStorage.setItem(k, v));
  } catch { }
}

/* ---------------- Role helpers ---------------- */
function readRoleKind(user = {}) {
  const raw = user.role ?? user.user_role ?? user.role_name ?? user.type ?? "";
  const roleNorm = String(raw).toLowerCase().replace(/\s+/g, " ").trim();
  const roleCompact = roleNorm.replace(/\s+/g, "");

  const isSuperByString = roleNorm === "super admin" || roleCompact === "superadmin";
  const isAdminByString = roleNorm === "admin" || isSuperByString;

  const isSuperByFlags =
    user.is_super === true ||
    user.is_super_admin === true ||
    user.super === true ||
    user.super_admin === true ||
    user.is_super === 1 ||
    user.is_super_admin === 1;

  const isAdminByFlags = user.is_admin === true || user.is_admin === 1;

  const isSuper = isSuperByString || isSuperByFlags;
  const isAdmin = isAdminByString || isAdminByFlags || isSuper;
  const isFinance = roleNorm === "finance";

  const isAllowed = isSuper || isAdmin || isFinance;
  const roleLabel = isSuper ? "super admin" : isAdmin ? "admin" : isFinance ? "finance" : String(raw || "").trim();

  return { isSuper, isAdmin, isFinance, isAllowed, roleLabel, rawRole: raw };
}

/* ---------------- Auth helpers ---------------- */
function isAuthed() {
  try {
    return Boolean(localStorage.getItem("access_token"));
  } catch {
    return false;
  }
}

/* ---------------- Small helpers ---------------- */
const norm = (v) => String(v ?? "").toLowerCase().trim();
function pickMsg(data) {
  return (
    data?.message ||
    data?.error ||
    data?.msg ||
    data?.detail ||
    data?.errors?.phone ||
    data?.errors?.password ||
    ""
  );
}

/**
 * ✅ Better error classification:
 * - 401 => password wrong (most common)
 * - 404 => phone not found / wrong phone
 * - 400 => try detect whether backend says phone/password
 * - fallback => show backend message
 */
function classifyLoginError(res, data) {
  const status = res?.status;
  const backendMsg = pickMsg(data);
  const m = norm(backendMsg);

  // If backend explicitly says phone / password, obey that first
  const mentionsPhone =
    m.includes("phone") || m.includes("mobile") || m.includes("number") || m.includes("user not found") || m.includes("not registered");
  const mentionsPassword =
    m.includes("password") || m.includes("pass") || m.includes("incorrect password") || m.includes("invalid password");

  if (mentionsPassword) {
    return { type: "password", message: backendMsg || "Incorrect password. Please try again." };
  }
  if (mentionsPhone) {
    return { type: "phone", message: backendMsg || "The entered phone number is not correct. Please check and try again." };
  }

  // Status-based fallbacks (clean UX)
  if (status === 401) {
    return { type: "password", message: "Incorrect password. Please enter the correct password." };
  }
  if (status === 404) {
    return { type: "phone", message: "The entered phone number is not correct or not registered." };
  }
  if (status === 400) {
    // If backend returned validation object
    const phoneErr = data?.errors?.phone;
    const passErr = data?.errors?.password;
    if (phoneErr) return { type: "phone", message: Array.isArray(phoneErr) ? phoneErr[0] : String(phoneErr) };
    if (passErr) return { type: "password", message: Array.isArray(passErr) ? passErr[0] : String(passErr) };
    // generic 400
    return { type: "other", message: backendMsg || "Please check your inputs and try again." };
  }

  return { type: "other", message: backendMsg || `Login failed (HTTP ${status || "???"}).` };
}

/* ============================== Component ============================== */
export default function Login() {
  const nav = useNavigate();

  const [banner, setBanner] = useState(null);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false); // 👁️
  const [loading, setLoading] = useState(false);

  // ✅ track phone dropdown open (to hide eye icon)
  const [phoneDropdownOpen, setPhoneDropdownOpen] = useState(false);

  const phoneInputRef = useRef(null);
  const itiRef = useRef(null);
  const restoreStorageRef = useRef(() => { });

  function waitForUtils(maxMs = 2000, interval = 50) {
    return new Promise((resolve) => {
      const start = Date.now();
      const tick = () => {
        if (window.intlTelInputUtils) return resolve(true);
        if (Date.now() - start >= maxMs) return resolve(false);
        setTimeout(tick, interval);
      };
      tick();
    });
  }

  // On mount: clear (but preserve "seen" keys) and block writes until login succeeds
  useEffect(() => {
    try {
      const keep = snapshotSeenKeys();
      localStorage.clear();
      restoreSeenKeys(keep);
    } catch { }
    restoreStorageRef.current = blockLocalStorageWrites();
    return () => {
      try {
        restoreStorageRef.current?.();
      } catch { }
    };
  }, []);

  // Harden back navigation on login page
  useEffect(() => {
    if (isAuthed()) {
      nav("/dashboard", { replace: true });
      return;
    }
    const pushBlock = () => {
      try {
        window.history.pushState(null, "", window.location.href);
      } catch { }
    };
    pushBlock();
    const onPop = () => {
      if (!isAuthed()) pushBlock();
    };
    window.addEventListener("popstate", onPop);
    const onPageShow = (e) => {
      if (e.persisted && !isAuthed()) window.location.reload();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [nav]);

  // Init phone input
  useEffect(() => {
    if (!phoneInputRef.current) return;

    itiRef.current = intlTelInput(phoneInputRef.current, {
      initialCountry: "bt",
      onlyCountries: ONLY_COUNTRIES,
      nationalMode: false,
      autoPlaceholder: "polite",
      formatOnDisplay: true,
      allowDropdown: true,
      imagePath: "/assets/img/",
      utilsScript: "https://cdn.jsdelivr.net/npm/intl-tel-input@18.5.6/build/js/utils.js",
    });

    // hide password eye when country dropdown opens
    const inputEl = phoneInputRef.current;

    const onOpen = () => setPhoneDropdownOpen(true);
    const onClose = () => setPhoneDropdownOpen(false);

    inputEl.addEventListener("open:countrydropdown", onOpen);
    inputEl.addEventListener("close:countrydropdown", onClose);

    return () => {
      try {
        itiRef.current?.destroy();
      } catch { }
      try {
        inputEl.removeEventListener("open:countrydropdown", onOpen);
        inputEl.removeEventListener("close:countrydropdown", onClose);
      } catch { }
    };
  }, []);

  /* ---------------- API helpers ---------------- */
  async function postLogin(phoneValue, password) {
    const res = await fetch(LOGIN_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phone: phoneValue, password }),
    });

    let data = {};
    try {
      data = await res.json();
    } catch { }

    return { res, data };
  }

  /**
   * ✅ Improved variant strategy:
   * - Try E164 first (best)
   * - Then raw
   * - Then digits-only, etc.
   * - DEDUPE
   * - If backend returns 400 "invalid phone", keep trying other variants
   * - If backend returns 401, DON'T keep trying other variants (password wrong), stop immediately.
   */
  async function tryAllVariants(variants, password) {
    const seen = new Set();
    const clean = (variants || [])
      .map((v) => String(v || "").trim())
      .filter(Boolean)
      .filter((v) => {
        const k = v;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });

    let last = null;

    for (const v of clean) {
      const { res, data } = await postLogin(v, password);

      if (res.ok) return { res, data, phoneUsed: v };

      // ✅ If 401, most likely password incorrect — stop looping variants
      if (res.status === 401) return { res, data, phoneUsed: v };

      // ✅ For 404/400, continue trying other formats (phone formatting mismatch)
      if (res.status === 404 || res.status === 400) {
        last = { res, data, phoneUsed: v };
        continue;
      }

      // other errors: stop
      return { res, data, phoneUsed: v };
    }

    return last;
  }

  /* ---------------- Phone helpers ---------------- */
  async function buildFacts(iti) {
    const utilsReady = await waitForUtils();
    const raw = (phoneInputRef.current?.value || "").trim();
    const hasPlus = raw.startsWith("+");

    const selected = iti.getSelectedCountryData() || {};
    const iso2 = (selected?.iso2 || "").toLowerCase();
    const dial = String(selected?.dialCode || "");

    const libE164 = (iti.getNumber() || "").trim();
    const digitsOnly = (libE164 || raw).replace(/\D/g, "");

    let nationalDigits = raw.replace(/\D/g, "");
    if (utilsReady && window.intlTelInputUtils) {
      try {
        const nat = iti.getNumber(window.intlTelInputUtils.numberFormat.NATIONAL) || "";
        nationalDigits = nat.replace(/\D/g, "") || nationalDigits;
      } catch { }
    }

    const e164Candidate = hasPlus
      ? libE164 || `+${digitsOnly}`
      : dial
        ? `+${dial}${nationalDigits}`
        : libE164;

    const nationalFromE164 = digitsOnly.startsWith(dial) ? digitsOnly.slice(dial.length) : nationalDigits;

    // ✅ lib validity if available
    let libValid = null;
    if (utilsReady && typeof iti.isValidNumber === "function") {
      try {
        libValid = iti.isValidNumber();
      } catch {
        libValid = null;
      }
    }

    return {
      utilsReady,
      raw,
      hasPlus,
      iso2,
      dial,
      libE164,
      e164: e164Candidate,
      digitsOnly,
      nationalDigits,
      nationalFromE164,
      libValid,
    };
  }

  function validatePerCountry(f) {
    const { raw, hasPlus, iso2, dial, digitsOnly, nationalFromE164, nationalDigits, libValid } = f;

    if (!raw) return { ok: false, msg: "Please enter your phone number." };

    // ✅ if user typed +, ensure it matches selected country dial (basic mismatch guard)
    if (hasPlus) {
      const d = String(dial || "");
      if (d && !digitsOnly.startsWith(d)) {
        return {
          ok: false,
          msg: `Selected country and number mismatch. Please use +${d} or switch country.`,
        };
      }
    }

    const rule = COUNTRY_RULES[iso2];
    const natToCheck = nationalFromE164 || nationalDigits;

    // ✅ Bhutan: strict rule
    if (iso2 === "bt") {
      const ok = COUNTRY_RULES.bt.national.test(natToCheck);
      if (!ok) {
        return { ok: false, msg: "For Bhutan: number must be 8 digits and start with 17, 16, or 77." };
      }
    } else {
      // ✅ Other countries:
      // - If utils are ready, trust library validity FIRST
      // - Else fallback to regex (if exists) + length sanity
      if (libValid === true) {
        // ok
      } else if (libValid === false) {
        return { ok: false, msg: "The entered phone number is not correct for the selected country." };
      } else {
        // utils not ready
        if (rule?.national) {
          if (!rule.national.test(natToCheck)) {
            return { ok: false, msg: "The entered phone number is not correct for the selected country." };
          }
        } else {
          const len = String(natToCheck || "").length;
          if (len < 6 || len > 15) {
            return { ok: false, msg: "The entered phone number is not correct." };
          }
        }
      }
    }

    const variants = [f.e164, f.libE164, f.raw, f.digitsOnly, f.nationalDigits]
      .map((v) => (v || "").trim())
      .filter(Boolean);

    return { ok: true, variants };
  }

  /* ---------------- Submit ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setBanner(null);

    const iti = itiRef.current;
    if (!iti) return;

    // ✅ Password check FIRST (so wrong/missing password never shows phone error)
    const pwd = String(password || "");
    if (!pwd.trim()) {
      setBanner({ type: "danger", message: "Please enter your password." });
      return;
    }

    // ✅ Then validate phone
    const facts = await buildFacts(iti);
    const verdict = validatePerCountry(facts);
    if (!verdict.ok) {
      setBanner({ type: "danger", message: verdict.msg });
      return;
    }

    setLoading(true);
    try {
      const loginResult = await tryAllVariants(verdict.variants, pwd);

      if (!loginResult || !loginResult.res) {
        throw new Error("Login failed. Please try again.");
      }

      if (!loginResult.res.ok) {
        const friendly = classifyLoginError(loginResult.res, loginResult.data);
        // ✅ show clean message: password vs phone
        setBanner({ type: "danger", message: friendly.message });
        return;
      }

      const data = loginResult.data;

      // ✅ Enforce roles on the client (admin or super admin)
      const { isSuper, isFinance, isAllowed, roleLabel, rawRole } = readRoleKind(data.user || {});
      if (REQUIRE_SUPER_ONLY) {
        if (!isSuper) {
          throw new Error(`Role mismatch. Only 'super admin' allowed. Detected: '${roleLabel || rawRole || "unknown"}'.`);
        }
      } else {
        if (!isAllowed) {
          throw new Error(`Role mismatch. Only 'admin' or 'super admin' allowed. Detected: '${roleLabel || rawRole || "unknown"}'.`);
        }
      }

      // allow localStorage writes now
      restoreStorageRef.current?.();

      // Persist tokens + user
      if (data.token?.access_token) localStorage.setItem("access_token", data.token.access_token);
      if (data.token?.refresh_token) localStorage.setItem("refresh_token", data.token.refresh_token);
      localStorage.setItem("user", JSON.stringify(data.user));

      // convenience fields
      const uid = data.user?.user_id ?? data.user?.id ?? data.user?._id;
      if (uid != null) {
        localStorage.setItem("user_id", String(uid));
        localStorage.setItem("admin_id", String(uid));
      }
      localStorage.setItem("is_super", String(isSuper));
      localStorage.setItem("is_finance", String(isFinance));
      // notify same-tab components (Sidebar, TopBar) that roles have changed
      window.dispatchEvent(new CustomEvent("auth-role-changed", {
        detail: { isSuper, isFinance }
      }));

      // friendly hello
      try {
        const token = data.token?.access_token || "";
        let userName = "";
        if (uid && token) {
          const res = await fetch(`${API_BASE_URL}/api/profile/${uid}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const prof = await res.json();
            userName = prof?.data?.user_name || prof?.user_name || "";
          }
        }
        Swal.fire({
          icon: "success",
          title: userName ? `Welcome back, ${userName}!` : "Welcome back!",
          text: "Ready to make today awesome?",
          timer: 1500,
          showConfirmButton: false,
          customClass: { title: "swal-title", popup: "swal-popup" },
        });
      } catch {
        Swal.fire({ icon: "success", title: "Welcome back!", timer: 1200, showConfirmButton: false });
      }

      // prevent back to login
      setTimeout(() => nav("/dashboard", { replace: true }), 800);
    } catch (err) {
      console.error("Login error:", err);
      setBanner({ type: "danger", message: err.message || "Login failed. Please try again." });

      // Clear but keep seen keys so the bell doesn't inflate counts next login
      try {
        const keep = snapshotSeenKeys();
        localStorage.clear();
        restoreSeenKeys(keep);
      } catch { }
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <div className="login-container">
      <div className="card-authentication fade-in">
        <div className="card-header">
          <img src="/assets/img/loginlogo.png" alt="Logo" className="logo-icon" />
          <h2 className="card-title">
            Welcome to <strong>TàbDey</strong>
          </h2>
          <p className="card-tagline">Sign in to your account</p>
        </div>

        <div className="card-body">
          {banner && (
            <div className={`alert alert-${banner.type} mb-3`} role="alert">
              {banner.message}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <i className="bi bi-telephone-fill input-icon"></i>
              <input
                ref={phoneInputRef}
                type="tel"
                id="loginPhone"
                className="form-control form-control--phone"
                placeholder="Phone Number"
                autoComplete="off"
                disabled={loading}
              />
            </div>

            {/* Password with eye toggle */}
            <div className="form-group password-group">
              <input
                type={showPassword ? "text" : "password"}
                className="form-control"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
              <i className="bi bi-lock-fill input-icon"></i>
              <i className="bi bi-lock-fill input-icon" style={{ left: "8px" }}></i>
              <i
                className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"} password-toggle ${phoneDropdownOpen ? "password-toggle--hidden" : ""
                  }`}
                onClick={() => setShowPassword((s) => !s)}
                role="button"
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={0}
              ></i>
            </div>

            <div className="form-footer">
              <Link to="/reset-password" className="forgot-password">
                Forgot password?
              </Link>
            </div>

            <button type="submit" className="btn-signin" disabled={loading}>
              {loading ? <span className="spinner-border spinner-border-sm" role="status" /> : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
